import { useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, FileUp, Loader2, RefreshCw } from 'lucide-react';
import { parseXERFile } from '../../services/xerParser';
import { parseXMLFile } from '../../services/xmlParser';
import type { ScheduleModel } from '../../types/schedule';

interface ScheduleUploadProps {
  onLoaded: (schedule: ScheduleModel) => void;
  isBaseline?: boolean;
  loaded?: ScheduleModel | null;
  onClear?: () => void;
}

const MAX_BYTES = 150 * 1024 * 1024;

/**
 * Drop zone for a .xer or P6 .xml file.
 *
 * Parsing happens on the main thread, so the progress callback reports the
 * stage it has reached rather than jumping 0 → 100; on a large file that is
 * the difference between "working" and "hung".
 */
export function ScheduleUpload({
  onLoaded,
  isBaseline = false,
  loaded,
  onClear,
}: ScheduleUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const processFile = async (file: File | undefined) => {
    if (!file) return;

    const extension = file.name.toLowerCase().split('.').pop();
    if (extension !== 'xer' && extension !== 'xml') {
      setError('Upload a Primavera .xer export or a P6 schema .xml file.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`That file is ${(file.size / 1024 / 1024).toFixed(0)}MB — the limit is 150MB.`);
      return;
    }

    setError(null);
    setIsProcessing(true);
    setProgress(0);
    setStage('Starting');

    // Let the spinner paint before the parse blocks the thread.
    await new Promise((resolve) => requestAnimationFrame(resolve));

    try {
      const onProgress = (percent: number, label?: string) => {
        setProgress(percent);
        if (label) setStage(label);
      };

      const schedule =
        extension === 'xer'
          ? await parseXERFile(file, onProgress)
          : await parseXMLFile(file, onProgress);

      if (schedule.activities.length === 0) {
        throw new Error('The file parsed but contained no activities.');
      }

      onLoaded(schedule);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not read that file. It may be an unsupported export format.'
      );
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setStage('');
      // Allow the same file to be re-selected after a failure.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (loaded && !isProcessing) {
    return (
      <div className="panel flex items-center gap-3 p-4">
        <CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: 'var(--status-pass)' }} />
        <div className="min-w-0 flex-1">
          <p className="eyebrow" style={{ color: 'var(--text-muted)' }}>
            {isBaseline ? 'Baseline' : 'Current schedule'}
          </p>
          <p
            className="truncate text-sm font-semibold"
            style={{ color: 'var(--text-primary)' }}
            title={loaded.fileName}
          >
            {loaded.fileName}
          </p>
          <p className="tnum text-xs" style={{ color: 'var(--text-muted)' }}>
            {loaded.activities.length.toLocaleString('en-GB')} activities ·{' '}
            {loaded.source.toUpperCase()}
          </p>
        </div>
        <button
          className="icon-button shrink-0"
          onClick={() => inputRef.current?.click()}
          title="Replace this file"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Replace
        </button>
        {onClear && (
          <button className="icon-button shrink-0" onClick={onClear}>
            Clear
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".xer,.xml"
          className="hidden"
          onChange={(e) => processFile(e.target.files?.[0])}
        />
      </div>
    );
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept=".xer,.xml"
        className="hidden"
        onChange={(e) => processFile(e.target.files?.[0])}
        disabled={isProcessing}
      />

      <button
        type="button"
        className="w-full rounded-sm border-2 border-dashed p-7 text-center transition-colors"
        style={{
          borderColor: isDragging ? 'var(--mr-vivid-blue)' : 'var(--border-strong)',
          background: isDragging ? 'rgba(0,115,255,0.06)' : 'var(--surface-card)',
          cursor: isProcessing ? 'wait' : 'pointer',
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          processFile(e.dataTransfer.files[0]);
        }}
        onClick={() => !isProcessing && inputRef.current?.click()}
        disabled={isProcessing}
      >
        {isProcessing ? (
          <div className="space-y-3">
            <Loader2
              className="mx-auto h-8 w-8 animate-spin"
              style={{ color: 'var(--mr-vivid-blue)' }}
            />
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {stage}
              </p>
              <div
                className="mx-auto mt-2 h-1 w-48 overflow-hidden rounded-sm"
                style={{ background: 'var(--surface-inset)' }}
              >
                <div
                  className="h-full transition-[width] duration-300"
                  style={{
                    width: `${progress}%`,
                    background: 'var(--mr-vivid-blue)',
                  }}
                />
              </div>
              <p className="tnum mt-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                {Math.round(progress)}%
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <FileUp
              className="mx-auto h-8 w-8"
              style={{ color: isDragging ? 'var(--mr-vivid-blue)' : 'var(--text-muted)' }}
            />
            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              {isBaseline ? 'Drop a baseline schedule' : 'Drop a current schedule'}
            </p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              .xer or P6 .xml · processed in your browser, never uploaded
              {isBaseline && ' · optional, unlocks variance and BEI'}
            </p>
          </div>
        )}
      </button>

      {error && (
        <div
          className="mt-2 flex items-start gap-2 rounded-sm p-3 text-xs"
          style={{ background: 'var(--status-fail-soft)', color: 'var(--status-fail)' }}
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
